"use client";

import { createContext, useCallback, useContext, useEffect, useLayoutEffect, useMemo, useRef, useSyncExternalStore, type ComponentProps, type ReactNode } from "react";
import dynamic from "next/dynamic";
import { usePathname, useRouter } from "next/navigation";
import { useStore } from "@/app/context/StoreContext";
import type TopNav from "@/app/flow-test/TopNav";
import type { SidebarInitialViews } from "@/lib/sidebar-views";
import { announceWorkspaceNavigation, createWorkspaceNavigationShellStore, workspaceHomePath, workspacePageId, WORKSPACE_NAVIGATION_START, WORKSPACE_PAGE_CREATION_START, type WorkspacePreviewHint } from "@/lib/client/workspace-navigation-shell";
import { beginPreparedWorkspaceNavigation } from "@/lib/client/workspace-navigation-activation";
import WorkspaceDocumentFrame from "./WorkspaceDocumentFrame";
import WorkspaceHomePreview from "./WorkspaceHomePreview";
import WorkspacePageHoverPreview from "./WorkspacePageHoverPreview";

type HeaderProps = ComponentProps<typeof TopNav>;
type ShellStore = ReturnType<typeof createWorkspaceNavigationShellStore<HeaderProps>>;
const Header = dynamic(() => import("@/app/flow-test/TopNav"), { ssr: false });
const Navigation = dynamic(() => import("@/app/flow-test/TopNav").then(module => module.PersistentWorkspaceNavigation), { ssr: false });
const ShellContext = createContext<ShellStore | null>(null);
const EMPTY: ReturnType<ShellStore["getSnapshot"]> = { pending: null, header: null };
const subscribeEmpty = () => () => {};
const getEmpty = () => EMPTY;

export function usePersistentWorkspaceHeader() {
  const shell = useContext(ShellContext);
  const id = workspacePageId(usePathname());
  return !!shell && !!id;
}

export function useWorkspaceShellState() {
  const shell = useContext(ShellContext);
  const state = useSyncExternalStore(shell?.subscribe ?? subscribeEmpty, shell?.getSnapshot ?? getEmpty, getEmpty);
  return { shell, ...state };
}

/** Commands/labels only: consumers do not need to rerender on header publication. */
export function useWorkspaceNavigationShell() {
  return useContext(ShellContext);
}

/** The route publishes verified header props; the header itself lives in layout.
 * No content or editor is retained after Next unmounts the old route. */
export function WorkspaceHeaderRegistration({ props }: { props: HeaderProps }) {
  const shell = useContext(ShellContext);
  const lease = useRef({});
  const routeId = workspacePageId(usePathname());
  useLayoutEffect(() => {
    if (props.pageId) shell?.publish(lease.current, { id: props.pageId, title: props.docTitle || "Без названия", icon: props.docIcon ?? "📦" }, props, routeId);
  }, [shell, props, routeId]);
  useLayoutEffect(() => {
    const token = lease.current;
    return () => shell?.release(token);
  }, [shell]);
  return null;
}

export default function WorkspaceNavigationShell({ initial, children }: { initial: SidebarInitialViews; children: ReactNode }) {
  const { user, account } = useStore();
  const identity = `${user?.id ?? ""}:${account?.id ?? ""}`;
  // Changing accounts discards all labels and callbacks from the old account.
  const shell = useMemo(() => {
    const next = createWorkspaceNavigationShellStore<HeaderProps>();
    next.remember(initial && initial.accountId === account?.id ? initial.workspacePages : []);
    return next;
    // A new identity gets a new store; list updates do not remount the shell.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [identity]);
  const pages = initial?.accountId === account?.id ? initial?.workspacePages ?? [] : [];
  useLayoutEffect(() => { shell.remember(pages); }, [shell, pages]);
  return <ShellContext.Provider value={shell}><ShellFrame initial={initial}>{children}</ShellFrame><WorkspacePageHoverPreview key={identity} identity={identity} getLabel={shell.label} /></ShellContext.Provider>;
}

function ShellFrame({ initial, children }: { initial: SidebarInitialViews; children: ReactNode }) {
  const { shell, pending, header, pendingHome } = useWorkspaceShellState();
  const pathname = usePathname();
  const routeId = workspacePageId(pathname);
  const router = useRouter();
  const { user, account, store, stores, mode } = useStore();
  const pages = initial?.accountId === account?.id ? initial?.workspacePages ?? [] : [];
  const openingHome = !!pendingHome && pendingHome.pathname !== pathname;
  const live = header?.page.id === routeId && !pending && !openingHome ? header : null;
  const label = pending ?? live?.page ?? pages.find(page => page.id === routeId);
  const documentActive = !!routeId || !!pending;
  const active = documentActive || openingHome;
  const waitingForRoute = !!pending && pending.id !== routeId;
  const rememberPreview = useCallback((id: string, hint: WorkspacePreviewHint) => shell?.rememberPreview(id, hint), [shell]);

  useEffect(() => {
    const begin = (event: Event) => {
      const detail = (event as CustomEvent<{ pageId?: string; href?: string }>).detail;
      if (detail?.pageId) shell?.begin(detail.pageId, routeId);
      else if (detail?.href && workspaceHomePath(detail.href)) shell?.beginHome(detail.href, pathname);
      else shell?.cancel();
    };
    const create = () => {
      if (!shell) return;
      const state = shell.getSnapshot();
      if (!state.pending && !state.pendingHome) return;
      // Cancel Next's in-flight destination now, before the creation POST
      // completes. Preserve the current editor and its existing save queue.
      beginPreparedWorkspaceNavigation(pathname);
      shell.cancel();
      router.replace(pathname);
    };
    const pop = () => {
      const href = window.location.pathname;
      announceWorkspaceNavigation(href);
      if (!workspacePageId(href) && !workspaceHomePath(href)) shell?.cancel();
    };
    window.addEventListener(WORKSPACE_NAVIGATION_START, begin);
    window.addEventListener(WORKSPACE_PAGE_CREATION_START, create);
    window.addEventListener("popstate", pop);
    return () => { window.removeEventListener(WORKSPACE_NAVIGATION_START, begin); window.removeEventListener(WORKSPACE_PAGE_CREATION_START, create); window.removeEventListener("popstate", pop); };
  }, [shell, routeId, pathname, router]);
  useEffect(() => { if (!routeId) shell?.cancel(); }, [routeId, pathname, shell]);
  useEffect(() => {
    // Home's tab title stays Next-owned: cancellation from a settings screen
    // must not leave a premature "Домашняя" title over the current route.
    if (!openingHome && label) document.title = `${label.title || "Без названия"} · FrogStat`;
  }, [label, openingHome]);
  // Cancellation stays available even offline. The old document keeps its
  // existing save queue until Next actually unmounts the route.

  const fallback: HeaderProps = {
    variant: "bar", pages, pageRoute: true, homeRoute: openingHome,
    pageId: openingHome ? undefined : label?.id ?? routeId ?? undefined,
    docTitle: openingHome ? "Домашняя" : label?.title || "Страница", docIcon: label?.icon ?? "📦",
    accountName: account?.name, storeName: mode === "all" ? "Все магазины" : store?.name ?? "FrogStat",
    userEmail: user?.email ?? "", stores: stores.map(item => ({ id: item.id, name: item.name })),
    storePinned: true, canEditPage: false, canChangeEditScope: false, navigationPending: true,
  };
  return <div data-workspace-sidebar-content>
    {active && initial && initial.accountId === account?.id && <Navigation initialPages={pages} initialItems={initial.items} pageId={openingHome ? undefined : pending?.id ?? routeId ?? undefined} homeRoute={openingHome} />}
    {active && <div data-workspace-shell-header className="flow-font fixed inset-x-0 top-0 z-40"><Header {...(live?.value ?? fallback)} /></div>}
    {/* Keep the old editor and its save queue mounted until Next commits.
        Hiding it for home navigation must not turn it into a new subtree. */}
    <div hidden={openingHome} inert={openingHome}>
      <WorkspaceDocumentFrame identity={`${user?.id ?? ""}:${account?.id ?? ""}`} active={documentActive}
        pageId={pending?.id ?? routeId} label={label} pending={!!pending} waitingForRoute={waitingForRoute}
        onPreviewHint={rememberPreview}
        onCancel={() => { beginPreparedWorkspaceNavigation(pathname); shell?.cancel(); router.replace(pathname); }}>
        {children}
      </WorkspaceDocumentFrame>
    </div>
    {openingHome && <WorkspaceHomePreview onCancel={() => { beginPreparedWorkspaceNavigation(pathname); shell?.cancel(); router.replace(pathname); }} />}
  </div>;
}
